import {
  clearStoredSession,
  isTokenExpired,
  loadStoredSession,
} from "./auth.storage";
import {
  AUTH_LOGOUT_PENDING_KEY,
  AUTH_LOGOUT_PENDING_MS,
} from "./auth.constants";
import {
  saveReturnToModuleId,
  saveReturnToPath,
} from "../modules/suite-navigation";

function isLogoutPending(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  try {
    const pending = sessionStorage.getItem(AUTH_LOGOUT_PENDING_KEY);
    if (!pending) return false;
    return Date.now() - Number(pending) < AUTH_LOGOUT_PENDING_MS;
  } catch {
    return false;
  }
}

export function markLogoutPending(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(AUTH_LOGOUT_PENDING_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearLogoutPending(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(AUTH_LOGOUT_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export interface SuiteLoginRedirectOptions {
  suiteUrl: string;
  moduleId?: string;
  returnTo?: string;
}

function normalizeSuiteBaseUrl(suiteUrl: string): string {
  return suiteUrl.replace(/\/$/, "").replace(/\/login$/, "");
}

function normalizeSuiteLoginUrl(suiteUrl: string): string {
  const base = suiteUrl.replace(/\/$/, "");
  return base.endsWith("/login") ? base : `${base}/login`;
}

function buildSuiteRelayUrl(options: SuiteLoginRedirectOptions): string {
  const params = new URLSearchParams();
  if (options.moduleId) params.set("targetModule", options.moduleId);
  if (options.returnTo?.startsWith("/")) params.set("targetPath", options.returnTo);
  const query = params.toString();
  return `${normalizeSuiteBaseUrl(options.suiteUrl)}/auth/relay${query ? `?${query}` : ""}`;
}

export function buildSuiteLoginUrl(options: SuiteLoginRedirectOptions): string {
  const login = normalizeSuiteLoginUrl(options.suiteUrl);
  if (!options.moduleId) return login;

  const params = new URLSearchParams({ module: options.moduleId });
  if (options.returnTo) {
    params.set("returnTo", options.returnTo);
  }
  return `${login}?${params.toString()}`;
}

/**
 * Redirige vers la connexion Suite (avec contexte module optionnel).
 * Passe toujours par /auth/relay pour éviter les boucles handoff ↔ login.
 */
export function redirectToSuiteLogin(options: SuiteLoginRedirectOptions): void {
  if (isLogoutPending()) return;

  if (options.moduleId) {
    saveReturnToModuleId(options.moduleId);
  }
  if (options.returnTo?.startsWith("/")) {
    saveReturnToPath(options.returnTo);
  }

  const session = loadStoredSession();
  if (session?.tokens?.accessToken && isTokenExpired(session.tokens.expiresAt)) {
    clearStoredSession();
  }

  window.location.replace(buildSuiteRelayUrl(options));
}

/** Après déconnexion produit : purge session Suite puis login. */
export function redirectAfterLogout(suiteUrl: string): void {
  markLogoutPending();
  window.location.replace(`${normalizeSuiteBaseUrl(suiteUrl)}/auth/logout`);
}
